/**
 * Alias interno para usuarios que inician sesión con número de teléfono.
 *
 * La user pool de Cognito solo admite EMAIL como identificador (inmutable),
 * así que el personal técnico —que solo tiene teléfono— vive en Cognito con
 * un username sintético con formato de correo derivado de su número:
 *
 *     +525512345678  ->  525512345678@tel.dohealth-epione.com
 *
 * El técnico nunca ve ese correo: escribe su teléfono y aquí se traduce.
 * ESTA REGLA ESTÁ DUPLICADA en el backend (`utils/phone_alias.py`): si
 * cambia allá, cambia aquí.
 */

import { AbstractControl, ValidationErrors } from '@angular/forms';

export const PHONE_USERNAME_DOMAIN = 'tel.dohealth-epione.com';

/** Lada por defecto: México. Números de 10 dígitos se asumen nacionales. */
export const DEFAULT_COUNTRY_CODE = '52';

/** Devuelve el número en E.164 (+52...) o null si no parece teléfono. */
export function normalizePhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const value = String(raw).trim();
  if (/[a-zA-Z@]/.test(value)) return null;

  const digits = value.replace(/\D/g, '');

  if (digits.length === 10) return `+${DEFAULT_COUNTRY_CODE}${digits}`;
  if (digits.length === 12 && digits.startsWith(DEFAULT_COUNTRY_CODE)) return `+${digits}`;
  // Otros países: se exige el + explícito y de 11 a 15 dígitos (E.164).
  if (value.startsWith('+') && digits.length >= 11 && digits.length <= 15) return `+${digits}`;
  return null;
}

/** E.164 -> username sintético. `phone` DEBE venir de normalizePhone. */
export function phoneToUsername(phone: string): string {
  return `${phone.replace(/^\+/, '')}@${PHONE_USERNAME_DOMAIN}`;
}

/** true si el "correo" es un alias sintético de teléfono. */
export function isPhoneUsername(value: string | null | undefined): boolean {
  return !!value && value.trim().toLowerCase().endsWith(`@${PHONE_USERNAME_DOMAIN}`);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Lo que el usuario escribió en "correo o teléfono" -> username de Cognito.
 * Correo pasa tal cual (en minúsculas); teléfono se vuelve alias sintético.
 */
export function toCognitoUsername(input: string): string {
  const value = (input ?? '').trim();
  const phone = normalizePhone(value);
  if (phone) return phoneToUsername(phone);
  return value.toLowerCase();
}

/** Validador: acepta un correo válido O un teléfono válido. */
export function emailOrPhoneValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value ?? '').trim();
  if (!value) return null; // `required` se encarga del vacío
  if (EMAIL_RE.test(value)) return null;
  if (normalizePhone(value)) return null;
  return { emailOrPhone: true };
}
